import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useSongs } from "../../../hooks/useSongs";

const API_BASE = process.env.REACT_APP_API_URL;

export const useAiQuestions = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: songs = [], isLoading: songsLoading } = useSongs();
  const [lookups, setLookups] = useState({
    questionBanks: [],
    difficulties: [],
    moods: [],
    topics: [],
    subtopics: [],
  });
  const [formState, setFormState] = useState({
    question_bank_id: "",
    songs_master_ids: [],
    question_count: 20,
    answer_types: [],
    difficulty_ids: [],
    mood_ids: [],
    topic_ids: [],
    subtopic_ids: [],
  });
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("");
  const [batchQuestions, setBatchQuestions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedTempQuestions, setSelectedTempQuestions] = useState([]);
  const [importedQuestions, setImportedQuestions] = useState([]);
  const [importedLoading, setImportedLoading] = useState(false);
  const [selectedBatchConfig, setSelectedBatchConfig] = useState(null);
  const [questionClues, setQuestionClues] = useState({});
  const [selectedSongsForClues, setSelectedSongsForClues] = useState({});
  const [generatingClues, setGeneratingClues] = useState({});

  const filteredSongs = useMemo(() => songs || [], [songs]);

  const groupedImportedByBatch = useMemo(() => {
    if (!importedQuestions || importedQuestions.length === 0) return {};
    return importedQuestions.reduce((acc, q) => {
      const batchId = q.source_batch_id || "Unassigned";
      if (!acc[batchId]) {
        acc[batchId] = [];
      }
      acc[batchId].push(q);
      return acc;
    }, {});
  }, [importedQuestions]);

  const fetchSubtopics = async (topicIds) => {
    if (!topicIds || topicIds.length === 0) {
      setLookups((prev) => ({ ...prev, subtopics: [] }));
      return [];
    }
    try {
      const responses = await Promise.all(
        topicIds.map((topicId) =>
          axios.get(
            `${API_BASE}/api/lookups/question-subtopic?topic_id=${topicId}`
          )
        )
      );
      const allSubtopics = responses.flatMap((res) => res.data || []);
      const activeSubtopics = allSubtopics.filter((st) => st.status === 1);
      setLookups((prev) => ({
        ...prev,
        subtopics: activeSubtopics,
      }));
      return activeSubtopics;
    } catch (error) {
      console.error("Failed to load sub-topics", error);
      setLookups((prev) => ({ ...prev, subtopics: [] }));
      return [];
    }
  };

  const loadLookups = async () => {
    try {
      const endpoints = [
        "question-bank",
        "difficulty",
        "question-mood",
        "question-topic",
      ];
      const responses = await Promise.all(
        endpoints.map((endpoint) =>
          axios.get(`${API_BASE}/api/lookups/${endpoint}`)
        )
      );
      const [questionBanks, difficulties, moods, topics] = responses.map(
        (res) => res.data || []
      );

      setLookups((prev) => ({
        ...prev,
        questionBanks: questionBanks.filter((qb) => qb.status === 1),
        difficulties: difficulties.filter((d) => d.status === 1),
        moods: moods.filter((m) => m.status === 1),
        topics: topics.filter((t) => t.status === 1),
      }));

      setFormState((prev) => ({
        ...prev,
        question_bank_id:
          prev.question_bank_id ||
          questionBanks.find((qb) => qb.bank_name === "Songq")?.bank_id ||
          questionBanks[0]?.bank_id ||
          "",
      }));
    } catch (error) {
      enqueueSnackbar("Failed to load lookup data", { variant: "error" });
    }
  };

  const fetchBatches = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/admin/ai-questions/batches`,
        { withCredentials: true }
      );
      setBatches(data || []);
      if (!selectedBatch && data?.length) {
        setSelectedBatch(data[0].batch_id);
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch batches", { variant: "error" });
    }
  };

  const fetchBatchQuestions = async (batchId) => {
    if (!batchId) return;
    setBatchLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/admin/ai-questions/batches/${batchId}/questions`,
        { withCredentials: true }
      );
      setBatchQuestions(data || []);
      setSelectedTempQuestions([]);
    } catch (error) {
      enqueueSnackbar("Failed to load batch questions", { variant: "error" });
    } finally {
      setBatchLoading(false);
    }
  };

  const fetchImported = async () => {
    setImportedLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/admin/ai-questions/imported`,
        { withCredentials: true }
      );
      setImportedQuestions(data || []);
    } catch (error) {
      enqueueSnackbar("Failed to load imported questions", {
        variant: "error",
      });
    } finally {
      setImportedLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormState((prev) => {
      const newState = { ...prev, [field]: value };

      if (field === "topic_ids") {
        newState.subtopic_ids = [];
        fetchSubtopics(value);
      }

      return newState;
    });
  };

  const handleGenerate = async () => {
    if (!formState.question_bank_id) {
      enqueueSnackbar("Please select a question bank", { variant: "warning" });
      return;
    }
    if (!formState.answer_types.length) {
      enqueueSnackbar("Select at least one answer type", { variant: "warning" });
      return;
    }

    setLoadingGenerate(true);
    try {
      const payload = {
        question_bank_id: formState.question_bank_id,
        question_count: Number(formState.question_count) || 20,
        answer_types: formState.answer_types,
        difficulty_ids: formState.difficulty_ids,
        mood_ids: formState.mood_ids,
        topic_ids: formState.topic_ids,
        subtopic_ids: formState.subtopic_ids,
      };
      await axios.post(
        `${API_BASE}/api/admin/ai-questions/generate`,
        payload,
        { withCredentials: true }
      );
      enqueueSnackbar("Questions generated successfully", {
        variant: "success",
      });
      await fetchBatches();
      return true; // Return true to indicate success
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.details ||
        "Failed to generate questions";
      enqueueSnackbar(message, { variant: "error" });
      return false;
    } finally {
      setLoadingGenerate(false);
    }
  };

  const updateQuestionStatus = async (tempQuestionId, status) => {
    try {
      await axios.post(
        `${API_BASE}/api/admin/ai-questions/temp/${tempQuestionId}/status`,
        { status },
        { withCredentials: true }
      );
      enqueueSnackbar("Status updated", { variant: "success" });
      setBatchQuestions((prev) =>
        prev.map((question) =>
          question.temp_question_id === tempQuestionId
            ? { ...question, status }
            : question
        )
      );
    } catch (error) {
      enqueueSnackbar("Failed to update status", { variant: "error" });
    }
  };

  const handlePublishSelected = async () => {
    if (!selectedTempQuestions.length) {
      enqueueSnackbar("Select at least one question to publish", {
        variant: "warning",
      });
      return;
    }

    try {
      await axios.post(
        `${API_BASE}/api/admin/ai-questions/publish`,
        { tempQuestionIds: selectedTempQuestions },
        { withCredentials: true }
      );
      enqueueSnackbar("Questions published successfully", {
        variant: "success",
      });
      setSelectedTempQuestions([]);
      fetchBatchQuestions(selectedBatch);
      fetchImported();
    } catch (error) {
      enqueueSnackbar("Failed to publish questions", { variant: "error" });
    }
  };

  const toggleTempQuestionSelection = (tempQuestionId) => {
    setSelectedTempQuestions((prev) =>
      prev.includes(tempQuestionId)
        ? prev.filter((id) => id !== tempQuestionId)
        : [...prev, tempQuestionId]
    );
  };

  const handleBulkSelectSongsForClues = (tempQuestionId, countOrAll) => {
    const existingClues = questionClues[tempQuestionId] || [];
    const existingIds = new Set(
      existingClues.map((c) => c.songs_master_id).filter(Boolean)
    );

    const availableSongs = filteredSongs.filter(
      (song) => !existingIds.has(song.songs_master_id)
    );

    if (!availableSongs.length) return;

    const sliceCount =
      countOrAll === "all" ? availableSongs.length : Number(countOrAll) || 0;
    if (!sliceCount) return;

    const nextSelection = availableSongs
      .slice(0, sliceCount)
      .map((song) => song.songs_master_id);

    setSelectedSongsForClues((prev) => ({
      ...prev,
      [tempQuestionId]: nextSelection,
    }));
  };

  const fetchQuestionClues = async (tempQuestionId) => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/admin/ai-questions/questions/${tempQuestionId}/clues`,
        { withCredentials: true }
      );
      setQuestionClues((prev) => ({
        ...prev,
        [tempQuestionId]: data.clues || [],
      }));
    } catch (error) {
      console.error("Error fetching clues:", error);
    }
  };

  const handleGenerateClues = async (tempQuestionId) => {
    const selectedSongIds = selectedSongsForClues[tempQuestionId] || [];
    if (selectedSongIds.length === 0) {
      enqueueSnackbar("Please select at least one song", { variant: "warning" });
      return;
    }

    setGeneratingClues((prev) => ({ ...prev, [tempQuestionId]: true }));
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/admin/ai-questions/questions/clues/generate`,
        {
          temp_question_id: tempQuestionId,
          songs_master_ids: selectedSongIds,
        },
        { withCredentials: true }
      );
      enqueueSnackbar(
        `Generated ${data.generated_clues} clue(s) successfully`,
        { variant: "success" }
      );
      await fetchQuestionClues(tempQuestionId);
      setSelectedSongsForClues((prev) => ({
        ...prev,
        [tempQuestionId]: [],
      }));
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.details ||
        "Failed to generate clues";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setGeneratingClues((prev) => ({ ...prev, [tempQuestionId]: false }));
    }
  };

  const handleDeleteClue = async (tempQuestionId, songId) => {
    try {
      await axios.delete(
        `${API_BASE}/api/admin/ai-questions/questions/${tempQuestionId}/clues/${songId}`,
        { withCredentials: true }
      );
      enqueueSnackbar("Clue deleted successfully", { variant: "success" });
      await fetchQuestionClues(tempQuestionId);
    } catch (error) {
      enqueueSnackbar("Failed to delete clue", { variant: "error" });
    }
  };

  const handleBatchChange = (batchId) => {
    setSelectedBatch(batchId);
    const selectedBatchData = batches.find((b) => b.batch_id === batchId);
    setSelectedBatchConfig(selectedBatchData?.config || null);
  };

  useEffect(() => {
    loadLookups();
    fetchBatches();
    fetchImported();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      fetchBatchQuestions(selectedBatch);
      const currentBatch = batches.find((b) => b.batch_id === selectedBatch);
      if (currentBatch) {
        setSelectedBatchConfig(currentBatch.config || null);
      } else {
        setSelectedBatchConfig(null);
      }
    }
  }, [selectedBatch]);

  useEffect(() => {
    if (batchQuestions.length > 0) {
      batchQuestions.forEach((question) => {
        fetchQuestionClues(question.temp_question_id);
      });
    }
  }, [batchQuestions]);

  return {
    // State
    lookups,
    formState,
    loadingGenerate,
    batches,
    selectedBatch,
    batchQuestions,
    batchLoading,
    selectedTempQuestions,
    importedQuestions,
    importedLoading,
    selectedBatchConfig,
    questionClues,
    selectedSongsForClues,
    generatingClues,
    filteredSongs,
    songsLoading,
    groupedImportedByBatch,
    // Actions
    handleFormChange,
    handleGenerate,
    updateQuestionStatus,
    handlePublishSelected,
    toggleTempQuestionSelection,
    handleBulkSelectSongsForClues,
    fetchQuestionClues,
    handleGenerateClues,
    handleDeleteClue,
    handleBatchChange,
    fetchBatches,
    fetchBatchQuestions,
    fetchImported,
    setSelectedSongsForClues,
  };
};
